import { MOTION, RADIUS } from "@elyxeer/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { dayStatusLabel } from "~/components/routine/day-status";
import { DosePips } from "~/components/routine/DosePips";
import { INTL_LOCALE, useLocale } from "~/i18n";
import {
  addMonths,
  buildMonthGrid,
  dayKey,
  startOfMonth,
  weekdayLabels,
} from "~/lib/calendar";
import { isFutureDay, type RoutineDay } from "~/lib/routine";

/**
 * The routine's month: when each dose was in, a day per cell.
 *
 * The same grid as the events calendar (`lib/calendar` does the arithmetic for both),
 * with the dose dots where the events calendar has its single event dot. Days still to
 * come are drawn but cannot be picked, and neither can a month that has not started.
 */
export function RoutineCalendar({
  month,
  today,
  selected,
  index,
  onSelect,
  onMonthChange,
}: {
  /** Any date inside the month to render. */
  month: Date;
  today: Date;
  selected: Date;
  index: Map<string, RoutineDay>;
  onSelect: (day: Date) => void;
  onMonthChange: (month: Date) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const intlLocale = INTL_LOCALE[locale];

  const weeks = buildMonthGrid(month, 1, today);
  const weekdays = weekdayLabels(intlLocale);
  const selectedKey = dayKey(selected);
  const isCurrentMonth =
    startOfMonth(month).getTime() === startOfMonth(today).getTime();

  const monthLabel = new Intl.DateTimeFormat(intlLocale, {
    month: "long",
    year: "numeric",
  }).format(month);
  const fullDate = new Intl.DateTimeFormat(intlLocale, { dateStyle: "full" });

  return (
    <Paper variant="outlined" sx={{ padding: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <IconButton
          onClick={() => onMonthChange(addMonths(month, -1))}
          aria-label={t("routine.previousMonth")}
          data-testid="routine.prev-month"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="L1"
          sx={{ color: "text.primary", textTransform: "capitalize" }}
          data-testid="routine.month"
        >
          {monthLabel}
        </Typography>

        <IconButton
          onClick={() => onMonthChange(addMonths(month, 1))}
          aria-label={t("routine.nextMonth")}
          data-testid="routine.next-month"
          // Nothing has been taken in a month that has not begun.
          disabled={isCurrentMonth}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {weekdays.map((label) => (
          <Typography
            key={label}
            variant="L3"
            aria-hidden="true"
            sx={{
              color: "text.muted",
              textAlign: "center",
              paddingBlock: 1,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        ))}

        {weeks.flat().map((cell) => {
          const isSelected = cell.key === selectedKey;
          const future = isFutureDay(cell.date, today);
          const doses = index.get(cell.key);

          return (
            <Box
              key={cell.key}
              component="button"
              type="button"
              disabled={future}
              onClick={() => onSelect(cell.date)}
              aria-label={
                future
                  ? fullDate.format(cell.date)
                  : dayStatusLabel(t, fullDate.format(cell.date), doses)
              }
              aria-current={cell.isToday ? "date" : undefined}
              aria-pressed={isSelected}
              data-testid={`routine.day-${cell.key}`}
              sx={{
                aspectRatio: "1",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                border: "1px solid transparent",
                borderRadius: `${RADIUS.sm}px`,
                cursor: future ? "default" : "pointer",
                font: "inherit",
                transition: `background-color ${MOTION.transition}, border-color ${MOTION.transition}`,
                backgroundColor: isSelected ? "primary.main" : "transparent",
                // Today is outlined rather than filled, so it never competes with the
                // selection for the same visual slot — the events calendar's rule.
                borderColor:
                  !isSelected && cell.isToday ? "primary.main" : "transparent",
                "&:hover:not(:disabled)": {
                  backgroundColor: isSelected
                    ? "primary.main"
                    : "surface.inset",
                },
              }}
            >
              <Typography
                variant="L2"
                sx={{
                  color: isSelected
                    ? "primary.contrastText"
                    : cell.inMonth && !future
                      ? "text.primary"
                      : "text.muted",
                  opacity: cell.inMonth ? 1 : 0.5,
                }}
              >
                {cell.date.getDate()}
              </Typography>

              {future ? (
                // Holds the row height, so the dates line up across the grid.
                <Box sx={{ height: 6 }} />
              ) : (
                <DosePips doses={doses} size={6} inverted={isSelected} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
